/**
 * Packages & addons — the Owner's commercial licensing surface
 * (README.md Part E2, Phase 28).
 *
 * A package bundles ADDON modules. Core capability is never listed and never
 * withheld, so there is no way for the Owner to accidentally sell a package
 * that cannot take a cash sale.
 */
import { z } from 'zod';
import { BUSINESS_MODULES } from '../constants.js';
import { idSchema } from './common.js';
/** Any subset of the addon catalog, de-duplicated. */
export const moduleListSchema = z
    .array(z.enum(BUSINESS_MODULES))
    .max(BUSINESS_MODULES.length)
    .transform((list) => [...new Set(list)]);
/**
 * Lowercase, hyphen-safe, stable. It is the human-facing key an Owner types
 * once and then never changes — renaming is what `name` is for.
 */
export const packageCodeSchema = z
    .string()
    .trim()
    .min(2)
    .max(32)
    .regex(/^[a-z0-9][a-z0-9-]*$/, 'Use lowercase letters, digits and hyphens');
export const createPackageSchema = z.object({
    code: packageCodeSchema,
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(500).optional(),
    modules: moduleListSchema.default([]),
    shopLimit: z.number().int().min(1).max(10_000).default(1),
    userLimit: z.number().int().min(1).max(100_000).default(5),
    monthlyPricePaisa: z.number().int().min(0).default(0),
    isDefault: z.boolean().default(false),
    isActive: z.boolean().default(true),
    sortOrder: z.number().int().min(0).max(10_000).default(0),
});
/** Everything except `code`, which is the stable key businesses are joined on. */
export const updatePackageSchema = createPackageSchema.omit({ code: true }).partial();
/**
 * What the Owner sets on one business: which package, plus any addons granted
 * on top of it for this customer only.
 *
 * `packageId: null` deliberately remains expressible — it returns a business to
 * the pre-package behaviour where its own `modulesEnabled` is the authority.
 */
export const assignPackageSchema = z.object({
    packageId: idSchema.nullable().optional(),
    addonModules: moduleListSchema.optional(),
});
export const listPackagesQuerySchema = z.object({
    includeInactive: z.coerce.boolean().default(false),
});
//# sourceMappingURL=packages.js.map